import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Calendar,
  DollarSign,
  TrendingUp,
  Bell,
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCcw,
  ChevronRight,
  Sparkles,
  Upload,
  Zap,
  FileSpreadsheet,
  Play,
  Eye,
  X,
  ArrowRight
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ============== Styled Components ==============

const Container = styled.div`
  min-height: calc(100vh - 200px);
  background: transparent;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  margin: 0.5rem 0 0 0;
`;

const AutoProcessBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  color: #10b981;
  font-size: 0.75rem;
  font-weight: 600;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled(motion.div)<{ $color: string }>`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid ${props => props.$color};
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StatInfo = styled.div``;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
`;

const EventsContainer = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const EventsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(15, 23, 42, 0.5);
`;

const EventsTitle = styled.h2`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  background: rgba(15, 23, 42, 0.5);
  padding: 4px;
  border-radius: 10px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? '#3b82f6' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#64748b'};

  &:hover {
    color: white;
  }
`;

const EventsList = styled.div`
  max-height: 500px;
  overflow-y: auto;
`;

const EventCard = styled(motion.div)<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const EventIcon = styled.div<{ $type: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  background: ${props => {
    switch (props.$type) {
      case 'Dividend': return 'rgba(16, 185, 129, 0.1)';
      case 'Interest Payment': return 'rgba(59, 130, 246, 0.1)';
      case 'Stock Split': return 'rgba(168, 85, 247, 0.1)';
      default: return 'rgba(100, 116, 139, 0.1)';
    }
  }};
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventType = styled.div<{ $type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  background: ${props => {
    switch (props.$type) {
      case 'Dividend': return 'rgba(16, 185, 129, 0.1)';
      case 'Interest Payment': return 'rgba(59, 130, 246, 0.1)';
      case 'Stock Split': return 'rgba(168, 85, 247, 0.1)';
      default: return 'rgba(100, 116, 139, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'Dividend': return '#10b981';
      case 'Interest Payment': return '#3b82f6';
      case 'Stock Split': return '#a855f7';
      default: return '#64748b';
    }
  }};
`;

const SecurityName = styled.div`
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
`;

const EventNotification = styled.div`
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 4px;
`;

const EventDates = styled.div`
  text-align: right;
  min-width: 120px;
`;

const DateLabel = styled.div`
  color: #64748b;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;

const DateValue = styled.div`
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
`;

const EventAmount = styled.div`
  text-align: right;
  min-width: 100px;
`;

const AmountValue = styled.div`
  color: #10b981;
  font-size: 1rem;
  font-weight: 700;
`;

const AmountLabel = styled.div`
  color: #64748b;
  font-size: 0.7rem;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  ${props => {
    switch (props.$status) {
      case 'Completed':
        return 'background: rgba(16, 185, 129, 0.1); color: #10b981;';
      case 'Upcoming':
        return 'background: rgba(59, 130, 246, 0.1); color: #3b82f6;';
      case 'Pending':
        return 'background: rgba(245, 158, 11, 0.1); color: #f59e0b;';
      case 'Ex-Date Passed':
        return 'background: rgba(168, 85, 247, 0.1); color: #a855f7;';
      default:
        return 'background: rgba(100, 116, 139, 0.1); color: #64748b;';
    }
  }}
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  color: #64748b;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  color: #3b82f6;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(59, 130, 246, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============== Interfaces ==============

interface RetailCorporateAction {
  id: number;
  eventType: string;
  status: string;
  securityName: string;
  symbol: string;
  exDate: string | null;
  paymentDate: string | null;
  rate: number | null;
  estimatedAmount: number | null;
  currency: string;
  splitRatio?: string;
  notification: string;
  autoProcess: boolean;
  processed: boolean;
}

// ============== Component ==============

const RetailCorporateActionsScreen: React.FC = () => {
  const [actions, setActions] = useState<RetailCorporateAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'upcoming' | 'completed'>('all');
  const [error, setError] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importCustodian, setImportCustodian] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);

  // Process modal state
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<RetailCorporateAction | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchActions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/corporate-actions/retail');
      setActions(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Error fetching corporate actions:', err);
      setError('Failed to load corporate actions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // Import corporate actions from custodian file
  const handleImport = async () => {
    if (!importFile) return;

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      if (importCustodian) {
        formData.append('custodian', importCustodian);
      }

      const response = await axios.post('/api/corporate-actions/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult({
        success: true,
        message: `Imported ${response.data.created} new events, updated ${response.data.updated} existing events.`
      });
      fetchActions();
    } catch (err: any) {
      setImportResult({
        success: false,
        message: err.response?.data?.error || 'Failed to import file'
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Generate corporate actions from current positions
  const handleGenerateFromPositions = async () => {
    setIsImporting(true);
    setImportResult(null);

    try {
      const response = await axios.post('/api/corporate-actions/generate-from-positions');

      setImportResult({
        success: true,
        message: `Generated ${response.data.created} new events from your holdings.`
      });
      fetchActions();
    } catch (err: any) {
      setImportResult({
        success: false,
        message: err.response?.data?.error || 'Failed to generate events'
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Open process modal and fetch preview
  const handleOpenProcessModal = async (action: RetailCorporateAction) => {
    setSelectedAction(action);
    setShowProcessModal(true);
    setProcessResult(null);
    setIsLoadingPreview(true);

    try {
      const response = await axios.get(`/api/corporate-actions/${action.id}/preview`);
      setPreview(response.data);
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      setPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Process the corporate action
  const handleProcess = async () => {
    if (!selectedAction) return;

    setIsProcessing(true);
    setProcessResult(null);

    try {
      const response = await axios.post(`/api/corporate-actions/${selectedAction.id}/process`);

      setProcessResult({
        success: true,
        message: response.data.message || 'Corporate action processed successfully!'
      });

      // Refresh the list after processing
      fetchActions();

      // Close modal after a short delay
      setTimeout(() => {
        setShowProcessModal(false);
        setSelectedAction(null);
        setPreview(null);
        setProcessResult(null);
      }, 2000);
    } catch (err: any) {
      setProcessResult({
        success: false,
        message: err.response?.data?.message || 'Failed to process corporate action'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredActions = actions.filter(action => {
    if (activeTab === 'upcoming') {
      return !action.processed && action.status !== 'Completed';
    }
    if (activeTab === 'completed') {
      return action.processed || action.status === 'Completed';
    }
    return true;
  });

  // Calculate statistics
  const upcomingCount = actions.filter(a => !a.processed && a.status !== 'Completed').length;
  const completedCount = actions.filter(a => a.processed || a.status === 'Completed').length;
  const totalExpected = actions
    .filter(a => !a.processed)
    .reduce((sum, a) => sum + (a.estimatedAmount || 0), 0);
  const totalReceived = actions
    .filter(a => a.processed)
    .reduce((sum, a) => sum + (a.estimatedAmount || 0), 0);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'Dividend': return '💰';
      case 'Interest Payment': return '💵';
      case 'Stock Split': return '📊';
      default: return '📋';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <Calendar className="w-7 h-7 text-emerald-500" />
            Dividends & Events
          </Title>
          <Subtitle>
            Track dividends, interest payments, and stock splits for your accounts
          </Subtitle>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-semibold text-white transition-all"
          >
            <Upload className="w-4 h-4" />
            Import from Custodian
          </button>
          <AutoProcessBadge>
            <Sparkles className="w-4 h-4" />
            Auto-Processing Enabled
          </AutoProcessBadge>
        </div>
      </Header>

      <StatsGrid>
        <StatCard $color="#3b82f6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <StatInfo>
            <StatLabel>Upcoming Events</StatLabel>
            <StatValue>{upcomingCount}</StatValue>
          </StatInfo>
          <StatIcon $color="#3b82f6">
            <Clock className="w-6 h-6" />
          </StatIcon>
        </StatCard>

        <StatCard $color="#10b981" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatInfo>
            <StatLabel>Completed</StatLabel>
            <StatValue>{completedCount}</StatValue>
          </StatInfo>
          <StatIcon $color="#10b981">
            <CheckCircle2 className="w-6 h-6" />
          </StatIcon>
        </StatCard>

        <StatCard $color="#f59e0b" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatInfo>
            <StatLabel>Expected Income</StatLabel>
            <StatValue>{formatCurrency(totalExpected)}</StatValue>
          </StatInfo>
          <StatIcon $color="#f59e0b">
            <TrendingUp className="w-6 h-6" />
          </StatIcon>
        </StatCard>

        <StatCard $color="#8b5cf6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatInfo>
            <StatLabel>Received YTD</StatLabel>
            <StatValue>{formatCurrency(totalReceived)}</StatValue>
          </StatInfo>
          <StatIcon $color="#8b5cf6">
            <DollarSign className="w-6 h-6" />
          </StatIcon>
        </StatCard>
      </StatsGrid>

      <EventsContainer>
        <EventsHeader>
          <EventsTitle>
            <Bell className="w-5 h-5 text-blue-500" />
            Event Activity
          </EventsTitle>
          <div className="flex items-center gap-4">
            <TabContainer>
              <Tab $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
                All Events
              </Tab>
              <Tab $active={activeTab === 'upcoming'} onClick={() => setActiveTab('upcoming')}>
                Upcoming
              </Tab>
              <Tab $active={activeTab === 'completed'} onClick={() => setActiveTab('completed')}>
                Completed
              </Tab>
            </TabContainer>
            <RefreshButton onClick={fetchActions} disabled={isLoading}>
              <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
              Refresh
            </RefreshButton>
          </div>
        </EventsHeader>

        <EventsList>
          {error && (
            <div className="p-4 m-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {!isLoading && filteredActions.length === 0 && (
            <EmptyState>
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No {activeTab === 'all' ? '' : activeTab} events found</p>
            </EmptyState>
          )}

          <AnimatePresence>
            {filteredActions.map((action, index) => (
              <EventCard
                key={action.id}
                $status={action.status}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <EventIcon $type={action.eventType}>
                  {getEventIcon(action.eventType)}
                </EventIcon>

                <EventInfo>
                  <EventType $type={action.eventType}>
                    {action.eventType}
                    {action.splitRatio && <span>({action.splitRatio})</span>}
                  </EventType>
                  <SecurityName>{action.securityName}</SecurityName>
                  <EventNotification>{action.notification}</EventNotification>
                </EventInfo>

                <EventDates>
                  <DateLabel>Payment Date</DateLabel>
                  <DateValue>{formatDate(action.paymentDate)}</DateValue>
                </EventDates>

                {action.estimatedAmount && (
                  <EventAmount>
                    <AmountValue>{formatCurrency(action.estimatedAmount)}</AmountValue>
                    <AmountLabel>Estimated</AmountLabel>
                  </EventAmount>
                )}

                <StatusBadge $status={action.status}>
                  {action.status}
                </StatusBadge>

                {/* Process button for non-completed actions */}
                {!action.processed && action.status !== 'Completed' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenProcessModal(action);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs font-semibold text-white transition-all"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Process
                  </button>
                )}

                <ChevronRight className="w-5 h-5 text-slate-600" />
              </EventCard>
            ))}
          </AnimatePresence>
        </EventsList>
      </EventsContainer>

      {/* Process Preview Modal */}
      {showProcessModal && selectedAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowProcessModal(false);
              setSelectedAction(null);
              setPreview(null);
              setProcessResult(null);
            }}
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-500" />
                  Process Corporate Action
                </h2>
                <button
                  onClick={() => {
                    setShowProcessModal(false);
                    setSelectedAction(null);
                    setPreview(null);
                    setProcessResult(null);
                  }}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Review the impact before processing
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Event Summary */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">
                    {getEventIcon(selectedAction.eventType)}
                  </div>
                  <div>
                    <div className="text-white font-semibold">{selectedAction.securityName}</div>
                    <div className="text-sm text-slate-400">{selectedAction.eventType}</div>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {isLoadingPreview && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCcw className="w-6 h-6 text-blue-500 animate-spin" />
                  <span className="ml-2 text-slate-400">Loading preview...</span>
                </div>
              )}

              {/* Preview Data */}
              {!isLoadingPreview && preview && (
                <>
                  {/* Summary */}
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <div className="text-blue-400 font-semibold text-sm mb-1">What will happen:</div>
                    <div className="text-white">{preview.summary}</div>
                  </div>

                  {/* Affected Positions */}
                  {preview.affectedPositions && preview.affectedPositions.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-slate-300 mb-2">
                        Affected Portfolios ({preview.affectedPositionCount})
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {preview.affectedPositions.map((pos: any, idx: number) => (
                          <div
                            key={idx}
                            className="bg-slate-800/50 rounded-lg p-3 border border-white/5"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-white font-medium text-sm">{pos.portfolioName}</span>
                              {pos.cashToReceive && (
                                <span className="text-emerald-400 font-bold">
                                  +${pos.cashToReceive.toFixed(2)}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 flex items-center gap-2">
                              {pos.currentQuantity && (
                                <span>{pos.currentQuantity.toLocaleString()} shares</span>
                              )}
                              {pos.newQuantity && (
                                <>
                                  <ArrowRight className="w-3 h-3" />
                                  <span className="text-amber-400">{pos.newQuantity.toLocaleString()} shares</span>
                                </>
                              )}
                              {pos.description && !pos.cashToReceive && !pos.newQuantity && (
                                <span>{pos.description}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Total Amount */}
                  {preview.totalAmount && preview.totalAmount > 0 && (
                    <div className="flex items-center justify-between py-3 border-t border-white/10">
                      <span className="text-slate-400">Total Amount:</span>
                      <span className="text-2xl font-bold text-emerald-400">
                        ${preview.totalAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Already Processed */}
              {!isLoadingPreview && preview?.alreadyProcessed && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-amber-400">
                  This corporate action has already been processed.
                </div>
              )}

              {/* Result Message */}
              {processResult && (
                <div className={cn(
                  "p-4 rounded-lg flex items-center gap-3",
                  processResult.success
                    ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
                    : "bg-red-500/10 border border-red-500/20 text-red-400"
                )}>
                  {processResult.success ? (
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  {processResult.message}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => {
                  setShowProcessModal(false);
                  setSelectedAction(null);
                  setPreview(null);
                  setProcessResult(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleProcess}
                disabled={isProcessing || isLoadingPreview || preview?.alreadyProcessed || processResult?.success}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <RefreshCcw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : processResult?.success ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Done!
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Process Now
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowImportModal(false)}
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/10">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-blue-500" />
                Import Corporate Actions
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Import dividends, interest payments, and other events from your custodian
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Custodian Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Custodian</label>
                <select
                  value={importCustodian}
                  onChange={(e) => setImportCustodian(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Auto-detect</option>
                  <option value="SCHWAB">Schwab Advisor Services</option>
                  <option value="FIDELITY">Fidelity Institutional</option>
                </select>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Corporate Action File</label>
                <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-blue-500/50 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.txt,.xlsx"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="ca-file-upload"
                  />
                  <label htmlFor="ca-file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      {importFile ? importFile.name : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">CSV, TXT, or XLSX</p>
                  </label>
                </div>
              </div>

              {/* Or Generate from Positions */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-slate-900 text-slate-500">or</span>
                </div>
              </div>

              <button
                onClick={handleGenerateFromPositions}
                disabled={isImporting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-white/10 rounded-lg text-white font-medium transition-all disabled:opacity-50"
              >
                <Zap className="w-4 h-4 text-amber-500" />
                Generate from Current Holdings
              </button>

              {/* Result Message */}
              {importResult && (
                <div className={cn(
                  "p-3 rounded-lg text-sm",
                  importResult.success
                    ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
                    : "bg-red-500/10 border border-red-500/20 text-red-400"
                )}>
                  {importResult.message}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isImporting ? (
                  <>
                    <RefreshCcw className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import File
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </Container>
  );
};

export default RetailCorporateActionsScreen;
